import type { BuyerMotionCard, OnboardingModeCard } from './platform-gtm-onboarding';
import { BUYER_MOTION_CARDS, ONBOARDING_MODE_CARDS } from './platform-gtm-onboarding';

export type IdentityStageId = 'guest' | 'member' | 'tenant';
export type EscalationTarget = 'none' | 'sales' | 'delivery_owner' | 'support';

export type ConciergeStateRecommendation = {
  buyerMotion: BuyerMotionCard;
  onboardingMode: OnboardingModeCard;
  identityStage: IdentityStageId;
  escalationTarget: EscalationTarget;
  escalationReason: string;
  requiredNow: string[];
  deferredLater: string[];
  starterPackage: string;
  trialPath: string;
  creditsMode: {
    label: string;
    summary: string;
    allowedActions: string[];
    gatedActions: string[];
  };
};

function getOnboardingModeById(id: OnboardingModeCard['id']): OnboardingModeCard {
  return ONBOARDING_MODE_CARDS.find((item) => item.id === id) ?? ONBOARDING_MODE_CARDS[1]!;
}

function getEscalationTarget(
  buyerMotionId: BuyerMotionCard['id'],
  stage: IdentityStageId,
): Pick<ConciergeStateRecommendation, 'escalationTarget' | 'escalationReason'> {
  if (stage === 'guest') {
    return {
      escalationTarget: 'none',
      escalationReason: 'guest 阶段优先让用户先看到价值，不要先把人推入人工流程。',
    };
  }

  if (buyerMotionId === 'ownership-sensitive') {
    return {
      escalationTarget: 'delivery_owner',
      escalationReason: '涉及 private deployment / model / source boundary，默认应转 delivery owner。',
    };
  }

  if (buyerMotionId === 'process-improvement' && stage === 'tenant') {
    return {
      escalationTarget: 'delivery_owner',
      escalationReason: 'tenant activation 阶段开始碰到 integration / rollout / module activation，应由 delivery owner 介入。',
    };
  }

  if (buyerMotionId === 'fast-roi' && stage === 'tenant') {
    return {
      escalationTarget: 'sales',
      escalationReason: 'Fast ROI 客户进入 tenant activation 时，通常会开始关心预算、upgrade 和 commercial packaging。',
    };
  }

  return {
    escalationTarget: 'none',
    escalationReason: '当前阶段可以由 AI concierge 继续推进，暂不需要人工升级。',
  };
}

function getRequiredNow(stage: IdentityStageId, motion: BuyerMotionCard): string[] {
  if (stage === 'guest') {
    return ['email / contact', 'invite intent', 'first value surface'];
  }
  if (stage === 'member') {
    return ['company name', 'role', 'buyer motion', 'immediate goal', motion.primaryDemoLabel];
  }
  return ['admin info', 'package choice', 'trust scope', 'risk class', 'billing mode'];
}

function getDeferredLater(stage: IdentityStageId): string[] {
  if (stage === 'guest') {
    return ['company profile', 'module config', 'credits plan', 'trust scope'];
  }
  if (stage === 'member') {
    return ['module deep setup', 'integration mapping', 'approval boundary', 'advanced sharing'];
  }
  return ['module-specific configuration', 'private deployment detail', 'source/license options', 'advanced automation policy'];
}

function getStarterPackage(motion: BuyerMotionCard['id']): string {
  switch (motion) {
    case 'fast-roi':
      return 'starter-warehouse-v1';
    case 'process-improvement':
      return 'starter-haulage-control-v1';
    case 'ownership-sensitive':
      return 'finance-foundation-v1 + sovereign path review';
    case 'network-driven':
      return 'network-member-pack-v1';
    default:
      return 'starter-core-v1';
  }
}

function getTrialPath(stage: IdentityStageId): string {
  switch (stage) {
    case 'guest':
      return 'share / portal / invite first';
    case 'member':
      return 'guided demo + quick-start package';
    case 'tenant':
      return 'package install + capability activation';
    default:
      return 'guided demo';
  }
}

function getCreditsMode(stage: IdentityStageId): ConciergeStateRecommendation['creditsMode'] {
  if (stage === 'guest') {
    return {
      label: 'No Credits Required Yet',
      summary: 'guest 先看共享价值，不要求先选择商业包。',
      allowedActions: ['share link access', 'portal visibility', 'guided invite'],
      gatedActions: ['package install', 'premium AI actions'],
    };
  }
  if (stage === 'member') {
    return {
      label: 'Starter Free Mode',
      summary: 'member 阶段默认进入 monthly free credits，用于 guided demo、trial 和轻量 AI action。',
      allowedActions: ['guided demo', 'starter package preview', 'basic AI guidance'],
      gatedActions: ['high-volume automation', 'premium OCR/analytics', 'advanced installs'],
    };
  }
  return {
    label: 'Tenant Credits / Paid Mode',
    summary: 'tenant 阶段需要明确 monthly credits、top-up 或 paid plan，基础访问不应被 hard-stop。',
    allowedActions: ['workspace access', 'basic usage', 'starter package operation'],
    gatedActions: ['premium AI compose', 'deep simulation', 'advanced runtime automation'],
  };
}

export function buildConciergeStateRecommendation(
  buyerMotionId: BuyerMotionCard['id'],
  identityStage: IdentityStageId,
): ConciergeStateRecommendation {
  const buyerMotion = BUYER_MOTION_CARDS.find((item) => item.id === buyerMotionId) ?? BUYER_MOTION_CARDS[0]!;
  const onboardingMode = getOnboardingModeById(buyerMotion.defaultOnboardingMode);
  const escalation = getEscalationTarget(buyerMotion.id, identityStage);

  return {
    buyerMotion,
    onboardingMode,
    identityStage,
    escalationTarget: escalation.escalationTarget,
    escalationReason: escalation.escalationReason,
    requiredNow: getRequiredNow(identityStage, buyerMotion),
    deferredLater: getDeferredLater(identityStage),
    starterPackage: getStarterPackage(buyerMotion.id),
    trialPath: getTrialPath(identityStage),
    creditsMode: getCreditsMode(identityStage),
  };
}
